import { NSConsignment } from 'Pages/UserDashboard/Consignment/Consignment.d';
import axios from 'axios';
import { AUTH_TOKEN_KEY, API_BASE_URL } from 'constants/index';
import { FormikErrors } from 'formik';
import { useEffect, useState } from 'react';

interface IUseFetchConsignmentListProps {
  pause?: boolean;
  status: NSConsignment.TTabValue;
  params: NSConsignment.IGetListParams;
  setParams: (
    values: React.SetStateAction<NSConsignment.IFormTabToolbar>,
    shouldValidate?: boolean | undefined,
  ) => Promise<void> | Promise<FormikErrors<NSConsignment.IFormTabToolbar>>;
}

const InitialParams = {
  page: 1,
  keyword: undefined,
  sort_by: 'createdAt_desc',
  category: undefined,
  order_status: undefined,
  sneakers_condition: undefined,
};

export const useFetchConsignmentList = (
  props: IUseFetchConsignmentListProps,
) => {
  const { pause, status, params, setParams } = props;
  const [isInitialFetch, setIsInitialFetch] = useState<boolean>(true);
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [data, setData] = useState<unknown>([]);
  const [parentData, setParentData] = useState<unknown>();
  const [response, setResponse] = useState<unknown>();
  const [stateParams, setStateParams] = useState<NSConsignment.IGetListParams>({
    ...InitialParams,
    ...params,
  });

  const fetching = async (forceParams?: NSConsignment.IGetListParams) => {
    setIsFetching(true);
    const tempParams = { ...stateParams };

    Object.assign(tempParams, {
      page: stateParams.page,
      ...forceParams,
    });

    const fetchResponse = await axios
      .get(`${API_BASE_URL}/users/consignments/${status}`, {
        headers: {
          Authorization: 'Bearer ' + localStorage.getItem(AUTH_TOKEN_KEY),
        },
        params: tempParams,
      })
      .then((res) => {
        if (res.data?.status_code !== 200) {
          return {
            isSuccess: false,
            message: res.data?.message,
            data: res.data?.data?.data,
            parentData: res.data?.data,
            response: res.data,
          };
        }

        return {
          isSuccess: true,
          message: res.data.message,
          data: res.data?.data?.data,
          parentData: res.data.data,
          response: res.data,
        };
      })
      .catch((_err) => {
        return {
          isSuccess: false,
          message: null,
          data: [],
          parentData: null,
          response: null,
        };
      });

    setData(fetchResponse.data);
    setParentData(fetchResponse.parentData);
    setResponse(fetchResponse.response);
    setTimeout(() => {
      setIsFetching(false);
    }, 1000);

    return fetchResponse;
  };

  const nextPage = () => {
    setStateParams({
      ...stateParams,
      page: stateParams.page + 1,
    });
  };

  const prevPage = () => {
    if (stateParams.page === 1) {
      return;
    }

    setStateParams({
      ...stateParams,
      page: stateParams.page - 1,
    });
  };

  const refresh = () => {
    setParams({
      ...stateParams,
      page: 1,
    });
  };

  useEffect(() => {
    if (!status || !params) {
      return;
    }

    if(isInitialFetch){
      setIsInitialFetch(false);
      return;
    }

    const overridePage = params.page !== stateParams.page ? {} : { page: 1 };
    setStateParams((prev) => {
      return {
        ...prev,
        ...params,
        ...overridePage,
      };
    });
  }, [params]);

  useEffect(() => {
    if (pause || !status) {
      return;
    }

    fetching();
  }, [pause, stateParams]);
  
  return {
    isFetching,
    data,
    parentData,
    response,
    fetching,
    nextPage,
    prevPage,
    refresh,
  };
};
